import { isDarkMode } from "./routes.tsx";
import { createParticipantColorAssigner } from "./participantColors.ts";

// Theme utility: call `t()` inside any render function to get the current theme colors.
// Because it reads `isDarkMode.value`, Preact signals will re-render the component on toggle.

export const t = () => {
  const dark = isDarkMode.value;
  return dark ? darkTheme : lightTheme;
};

// Convenience: primary brand color
export const primaryColor = () => isDarkMode.value ? "#7ec7ff" : "#007aff";

// --- Participant color palette ---
// 5 colors chosen for good contrast in both light and dark modes.
// Each has a dark-mode bg/text pair and a light-mode bg/text pair.
const participantPalette = [
  {
    dark: { bg: "hsl(210, 32%, 18%)", text: "#ffffff", label: "#6cb8f0" },
    light: { bg: "#d4ecff", text: "#1a1a2e", label: "#1a6fb5" },
  }, // Blue
  {
    dark: { bg: "hsl(145, 32%, 18%)", text: "#ffffff", label: "#6ee09a" },
    light: { bg: "#d4f8e4", text: "#1a1a2e", label: "#1a8f50" },
  }, // Green
  {
    dark: { bg: "hsl(275, 32%, 18%)", text: "#ffffff", label: "#b88cf0" },
    light: { bg: "#ece0ff", text: "#1a1a2e", label: "#6b3fa0" },
  }, // Purple
  {
    dark: { bg: "hsl(25, 32%, 18%)", text: "#ffffff", label: "#e0a06e" },
    light: { bg: "#ffe8d4", text: "#1a1a2e", label: "#a05a20" },
  }, // Orange
  {
    dark: { bg: "hsl(340, 32%, 18%)", text: "#ffffff", label: "#e06e9a" },
    light: { bg: "#ffe0ec", text: "#1a1a2e", label: "#a03060" },
  }, // Pink
];

// Assigns colors to participants in first-appearance order so that:
// 1. A participant's color never changes as more messages load.
// 2. No two participants share a color (up to palette.length participants).
// Call `resetParticipantColors()` whenever a new conversation is opened.
const assigner = createParticipantColorAssigner(participantPalette.length);

export const resetParticipantColors = assigner.reset;

export const participantColor = (
  name: string,
  _allParticipants?: string[],
): { bg: string; text: string; label: string } => {
  const entry = participantPalette[assigner.getIndex(name)];
  return isDarkMode.value ? entry.dark : entry.light;
};

const darkTheme = {
  // Page backgrounds
  pageBg: "#121212",
  surfaceBg: "#181c20",
  cardBg: "#1e2228",
  cardBgAlt: "#23272e",
  cardBgAccent: "#1a2332",
  navBg: "#14181c",

  // Borders
  border: "#2a2d36",
  borderLight: "#333",
  borderAccent: "#2d4a6f",

  // Text
  text: "#e6e6e6",
  textBright: "#fff",
  textMuted: "#aaa",
  textDim: "#888",
  textFaint: "#666",
  textSubtle: "#555",

  // Interactive
  primary: "#7ec7ff",
  primaryBg: "rgba(126, 199, 255, 0.1)",
  accent: "#f6ad55",
  accentBg: "#2d3748",
  danger: "#ff6b6b",
  dangerBg: "#5a2020",
  dangerBorder: "#882222",

  // Chart / stats
  statBlue: "#7ec7ff",
  statGreen: "#5ee6b8",
  statOrange: "#ffb454",

  // Inputs / controls
  inputBg: "#23262f",
  inputBorder: "#444",
  hoverBg: "#31344a",

  // SVG / chart
  gridLine: "#333",
  axisLine: "#555",
  axisText: "#888",
};

const lightTheme = {
  pageBg: "#f8f9fa",
  surfaceBg: "#ffffff",
  cardBg: "#ffffff",
  cardBgAlt: "#f5f5f5",
  cardBgAccent: "#eef4fb",
  navBg: "#f0f2f5",

  border: "#dde1e6",
  borderLight: "#e0e0e0",
  borderAccent: "#b8d4ee",

  text: "#1a1a2e",
  textBright: "#111",
  textMuted: "#555",
  textDim: "#777",
  textFaint: "#999",
  textSubtle: "#aaa",

  primary: "#007aff",
  primaryBg: "rgba(0, 122, 255, 0.08)",
  accent: "#c87000",
  accentBg: "#fff3e0",
  danger: "#d32f2f",
  dangerBg: "#fff0f0",
  dangerBorder: "#ffcdd2",

  statBlue: "#007aff",
  statGreen: "#2e9e7a",
  statOrange: "#d48800",

  inputBg: "#f5f5f5",
  inputBorder: "#d0d0d0",
  hoverBg: "#e8ecf0",

  gridLine: "#e0e0e0",
  axisLine: "#ccc",
  axisText: "#888",
};
